import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import { itemPassesAuthTest } from '../../authorizations/auth-helpers';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { selectors as routeSelectors } from '../../routing';
import { availableAuthSitesSelector } from '../../app/store/selectors';
import {
    calcDefaultValue,
    isItemDirty,
    isGroupDirty,
    generateDefaultData,
    calcMultiItemViewValue,
} from '../../util/data-helpers';
import { buildHistoryRecords } from '../../util/history-helpers';
import { DETAIL_MODES } from '../../form/constants';
import { SELECT_MODES } from '../../table/table-constants';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../util/constants';
import { USER_TEMPLATE_TYPES } from './constants';

export const userNameSelector = createSelector(
    routeSelectors.currentRouteName,
    routeSelectors.currentRouteData,
    (routeName, routeData) => {
        if (routeName === 'user') {
            return routeData.user;
        }
    }
);

export const userIsLoadingSelector = (state) => state.user.isLoading;
export const userIsSavingSelector = (state) => state.user.isSaving;
export const selectedUserIdsSelector = (state) => state.user.selectedUserIds || EMPTY_ARRAY;
export const userListFiltersSelector = (state) => state.user.filters || EMPTY_OBJECT;

export const userValidatedPathsSelector = (state) => state.user.validatedPaths || EMPTY_ARRAY;
export const userValidationErrorsSelector = (state) => state.user.validationErrors || EMPTY_OBJECT;
export const userDetailModeSelector = (state) => state.user.detailMode || DETAIL_MODES.VIEW;

export const activeAuthSiteNameSelector = (state) => state.user?.activeAuthSite;
export const userSelectMode = (state) => state.user?.selectMode;

export const userListSelector = (state) => state.user.users || EMPTY_ARRAY;
export const isLoadingUsersSelector = (state) => state.user.isLoadingUsers;
export const allUserListSelector = (state) => state.user.allUsers || EMPTY_ARRAY;
export const isLoadingAllUsersSelector = (state) => state.user.isLoadingAllUsers;
export const userTemplateListIsLoadingSelector = (state) => state.user.templateListIsLoading;
export const userDevicesSelector = (state) => state.user.userDevices || EMPTY_ARRAY;
export const userHistorySelector = (state) => state.user.userHistory || EMPTY_ARRAY;
export const isLoadingUserHistorySelector = (state) => !!state.user.isLoadingUserHistory;

export const activeAuthSiteDetailsSelector = createSelector(
    availableAuthSitesSelector,
    activeAuthSiteNameSelector,
    (availableAuthSites, activeAuthSiteName) =>
        availableAuthSites.find((site) => site.name === activeAuthSiteName)
);

const userConfigTemplatesSelector = (state) => state.user.templates || EMPTY_OBJECT;

export const userConfigTemplateSelectorFactory = (templateType = USER_TEMPLATE_TYPES.GENERAL) =>
    createSelector(
        userConfigTemplatesSelector,
        activeAuthSiteDetailsSelector,
        userSelectMode,
        (templates, activeAuthSite, selectMode) => {
            const template = templates[templateType];
            if (!template) {
                return EMPTY_OBJECT;
            }
            // clone required b/c we may need to mondify for auths
            // should some part of this be pulled out ?
            const clonedTemplate = cloneDeep(template);
            if (activeAuthSite) {
                resolveTemplateAuths(clonedTemplate, activeAuthSite.authorizations);
            }
            if (selectMode === SELECT_MODES.MULTI) {
                removeSingleEditTemplateItems(clonedTemplate);
            }
            return clonedTemplate;
        }
    );

export const userConfigTemplateSelector = userConfigTemplateSelectorFactory(
    USER_TEMPLATE_TYPES.GENERAL
);

const userHistoryConfigTemplateSelector = userConfigTemplateSelectorFactory(
    USER_TEMPLATE_TYPES.HISTORY
);

function resolveTemplateAuths(group, authorizations = [], parentPath = []) {
    Object.entries(group).forEach(([key, itemTemplate]) => {
        const isObjectItem = typeof itemTemplate === 'object';
        if (isObjectItem) {
            const path = [...parentPath.slice(0), key];
            if (itemTemplate?.type === 'GROUP' && itemTemplate?.properties) {
                resolveTemplateAuths(itemTemplate.properties, authorizations, path);
            }
            // we can build this out as we go, for now only the selects have auths
            // check for auths, update as necessary
            if (itemTemplate.type === 'SELECT' || itemTemplate.type === 'MULTISELECT') {
                const { optionsAuthRequired, options } = itemTemplate;
                if (optionsAuthRequired?.length) {
                    let updatedOptions = options;
                    optionsAuthRequired.forEach((optionItem) => {
                        if (itemPassesAuthTest(optionItem, authorizations)) {
                            updatedOptions = [...updatedOptions, ...optionItem.options];
                        }
                    });
                    itemTemplate.options = updatedOptions;
                }
            }
        }
    });
}

// remove any items that don't allow bulk edit
function removeSingleEditTemplateItems(group, authorizations = [], parentPath = []) {
    Object.entries(group).forEach(([key, itemTemplate]) => {
        const isObjectItem = typeof itemTemplate === 'object';
        if (isObjectItem) {
            const path = [...parentPath.slice(0), key];
            if (itemTemplate?.type === 'GROUP' && itemTemplate?.properties) {
                removeSingleEditTemplateItems(itemTemplate.properties, authorizations, path);
            }
            if (itemTemplate.notBulkEditable) {
                delete group[key];
            }
        }
    });
}

export const activeAuthSiteIdSelector = createSelector(
    activeAuthSiteDetailsSelector,
    (activeAuthSiteDetails) => activeAuthSiteDetails?.eSiteId
);

export const userPassedValidationSelectorFactory = (path) =>
    createSelector(userValidatedPathsSelector, (validatedPaths) => {
        return validatedPaths.includes(path);
    });

export const isUserAdminReadySelector = createSelector(
    isLoadingUsersSelector,
    userTemplateListIsLoadingSelector,
    (userListLoading, userTemplatesLoading) => !userListLoading && !userTemplatesLoading
);

export const selectedUserDataSelector = createSelector(
    userListSelector,
    selectedUserIdsSelector,
    userSelectMode,
    (users, selectedUserIds, selectMode) => {
        if (!selectedUserIds?.length) {
            return EMPTY_OBJECT;
        }
        // in multi-user mode we want the selected user to be EMPTY for the
        // base user data in order to re-use much of the single user code
        if (selectMode === SELECT_MODES.MULTI) {
            return EMPTY_OBJECT;
        }
        const firstSelectedUserId = selectedUserIds[0];
        const selectedUser = users.find((u) => u.eUserId === firstSelectedUserId);
        // here we need to only supply template fields?
        return selectedUser || EMPTY_OBJECT;
    }
);

const _stagedEditsSelector = (state) => state.user.stagedEdits || EMPTY_OBJECT;

// defaults selector based on templates
export const defaultUserDataSelector = createSelector(userConfigTemplateSelector, (userTemplate) =>
    generateDefaultData(userTemplate)
);

export const userStagedEditsSelector = createSelector(
    selectedUserDataSelector,
    _stagedEditsSelector,
    defaultUserDataSelector,
    userDetailModeSelector,
    userSelectMode,
    (userData, stagedEdits, defaultData, detailMode, selectMode) => {
        // for select mode, we just want the staged edits
        if (selectMode === SELECT_MODES.MULTI) {
            return stagedEdits;
        }
        const isEditOrNewMode = detailMode !== DETAIL_MODES.VIEW;
        if (isEditOrNewMode) {
            const merged = mergeWith({}, defaultData, userData, stagedEdits, customizer);
            return merged;
        }
        return userData;
    }
);

function customizer(objValue, srcValue) {
    if (isArray(objValue) || isArray(srcValue)) {
        return srcValue;
    }
}

export const userElementValueFactory = (path, itemTemplate) =>
    createSelector(
        selectedUserDataSelector,
        userStagedEditsSelector,
        userDetailModeSelector,
        (userData, stagedEdits, detailMode) => {
            const defaultValue = calcDefaultValue(itemTemplate);
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            const sourceData = isViewMode ? userData : stagedEdits;
            return get(sourceData, path, defaultValue);
        }
    );

export const userElementIsReadOnlyFactory = (path, itemTemplate) =>
    createSelector(() => {
        //const isViewMode = detailMode === DETAIL_MODES.VIEW;
        //const isEditMode = detailMode === DETAIL_MODES.EDIT;
        return itemTemplate.notEditable;
        // not sure I want the view mode check too but for now we'll do it that way
        //return isViewMode || (isEditMode && itemTemplate.notEditable);
    });

export const userElementIsDirtyFactory = (path, itemTemplate) =>
    createSelector(
        selectedUserDataSelector,
        userStagedEditsSelector,
        userDetailModeSelector,
        (userData, stagedEdits, detailMode) => {
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            if (isViewMode) {
                return false;
            }
            return isItemDirty(itemTemplate, path, userData, stagedEdits);
        }
    );

export const isActiveRecordDirty = createSelector(
    userConfigTemplateSelector,
    selectedUserDataSelector,
    userStagedEditsSelector,
    userDetailModeSelector,
    (userTemplate, userData, stagedEdits, detailMode) => {
        const isViewMode = detailMode === DETAIL_MODES.VIEW;
        if (isViewMode) {
            return false;
        }
        if (isEmpty(stagedEdits)) {
            return false;
        }
        return isGroupDirty(userTemplate, userData, stagedEdits);
    }
);

export const userElementErrorFactory = (path) =>
    createSelector(userValidationErrorsSelector, (validationErrors) => {
        return get(validationErrors, path);
    });

export const bulkUserApplyPathsSelector = (state) => state.user.bulkApplyPaths || EMPTY_ARRAY;

export const bulkUserElementApplyFactory = (path) =>
    createSelector(bulkUserApplyPathsSelector, (paths) => {
        return paths.includes(path);
    });

const bulkUserSelectedUserDataSelector = createSelector(
    userListSelector,
    selectedUserIdsSelector,
    userDetailModeSelector, // todo remove I think this isn't used now
    (users, selectedUserIds, detailMode) => {
        if (!selectedUserIds?.length) {
            return EMPTY_ARRAY;
        }
        return users.filter((u) => selectedUserIds.includes(u.eUserId));
    }
);

export const bulkUserElementSummaryValueFactory = (path, itemTemplate) =>
    createSelector(bulkUserSelectedUserDataSelector, (allSelectedUsersData) =>
        calcMultiItemViewValue(itemTemplate, path, allSelectedUsersData)
    );

// this valueFactory is the thing that does the work for bulk edit values
// it either returns the selected values with defaults or a calculated representation of all selected users
export const bulkUserElementValueFactory = (path, itemTemplate) =>
    createSelector(
        _stagedEditsSelector,
        bulkUserElementApplyFactory(path),
        bulkUserElementSummaryValueFactory(path, itemTemplate),
        (stagedEdits, bulkApply, summaryValue) => {
            const defaultValue = calcDefaultValue(itemTemplate);
            if (bulkApply) {
                return get(stagedEdits, path, defaultValue);
            }
            return summaryValue;
        }
    );

// used in save
export const bulkUserDataSelector = createSelector(
    _stagedEditsSelector,
    bulkUserApplyPathsSelector,
    selectedUserIdsSelector,
    defaultUserDataSelector,
    (stagedEdits, bulkApplyPaths, selectedUserIds, defaultData) => {
        if (!(bulkApplyPaths?.length && selectedUserIds?.length)) {
            return false;
        }
        // forAach path, need to do the edit or default thing
        const data = {};
        bulkApplyPaths.forEach((path) => {
            const defaultValue = get(defaultData, path);
            const value = get(stagedEdits, path, defaultValue);
            set(data, path, value);
        });
        return {
            data,
            eUserIds: selectedUserIds,
        };
    }
);

export const userHistoryRecordsSelector = createSelector(
    userHistorySelector,
    userHistoryConfigTemplateSelector,
    (userHistory, userHistoryTemplate) => buildHistoryRecords(userHistory, userHistoryTemplate)
);
